import React, { useState, useEffect } from "react";
import {
  Calendar as CalendarIcon,
  Plus,
  ChevronRight,
  Folder,
  Eye,
  Edit2,
  Trash2,
  Loader2,
  AlertCircle,
} from "lucide-react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  useGetCalendarsQuery,
  useDeleteCalendarMutation,
} from "../../slices/calendarApiSlice";
import { useGetUserByEmailQuery } from "../../slices/userApiSlice";
import { toast } from "sonner";

const CalendarOverview = () => {
  const navigate = useNavigate();

  // Get user role and academy from Redux
  const userRole = useSelector((state) => state.auth.userRole);
  const userInfo = useSelector((state) => state.auth.userInfo);
  const userEmail = userInfo?.username || userInfo?.email;
  const canEdit = [
    "cfm",
    "CFM",
    "admin",
    "Admin",
    "super_admin",
    "Super Admin",
  ].includes(userRole);

  // Get user data from API to get actual academy ID
  const { data: userByEmail } = useGetUserByEmailQuery(userEmail, {
    skip: !userEmail,
  });

  // Get academy ID from API data
  const academyId =
    userByEmail?.user?.academyId || userInfo?.academy_id || userInfo?.academyId;

  // Fetch calendars from API
  const {
    data: calendars = [],
    isLoading,
    error,
    refetch,
  } = useGetCalendarsQuery({ academy_id: academyId });

  // Console log calendar details
  useEffect(() => {
    console.log("=== Calendar Overview - Calendar Details ===");
    console.log("Academy ID:", academyId);
    console.log("Calendars Data:", calendars);
    console.log("Is Loading:", isLoading);
    console.log("Error:", error);
    console.log("Calendars type:", typeof calendars);
    console.log("Is Array:", Array.isArray(calendars));
    console.log("Calendars value:", calendars);
    console.log(
      "Number of Calendars:",
      Array.isArray(calendars) ? calendars.length : 0,
    );
    if (Array.isArray(calendars) && calendars.length > 0) {
      console.log("Calendar Details:");
      calendars.forEach((calendar, index) => {
        console.log(`Calendar ${index + 1}:`, {
          id: calendar._id || calendar.id,
          year: calendar.year,
          name: calendar.name,
          holiday_count: calendar.holiday_count,
          working_days: calendar.working_days,
          fullData: calendar,
        });
      });
    }
    console.log("===========================================");
  }, [calendars, academyId, isLoading, error]);

  // Delete calendar mutation
  const [deleteCalendar, { isLoading: isDeleting }] =
    useDeleteCalendarMutation();

  // Color options for calendars
  const calendarColors = [
    { name: "Blue", value: "#3b82f6" },
    { name: "Green", value: "#10b981" },
    { name: "Purple", value: "#8b5cf6" },
    { name: "Red", value: "#ef4444" },
    { name: "Orange", value: "#f59e0b" },
    { name: "Pink", value: "#ec4899" },
  ];

  const handleCreateCalendar = () => {
    if (!canEdit) return;

    const pathSegments = window.location.pathname.split("/").filter(Boolean);
    const roleBasePath = pathSegments[0];
    navigate(`/${roleBasePath}/calendar/create`);
  };

  const handleDeleteCalendar = async (calendarId) => {
    if (!canEdit) return;

    const calendar = calendars.find(
      (cal) => cal._id === calendarId || cal.id === calendarId,
    );

    if (!calendar) return;

    // Show confirmation dialog
    toast("Delete Calendar", {
      description: `Are you sure you want to delete "${calendar.name}"? This will also delete all holidays in this calendar.`,
      action: {
        label: "Yes, delete it!",
        onClick: async () => {
          try {
            await deleteCalendar(calendarId).unwrap();
            toast.success("Calendar has been deleted successfully.");
          } catch (error) {
            console.error("Error deleting calendar:", error);
            toast.error("Error!", {
              description:
                error?.data?.message ||
                "Failed to delete calendar. Please try again.",
            });
          }
        },
      },
    });
  };

  const handleCalendarClick = (calendar) => {
    const pathSegments = window.location.pathname.split("/").filter(Boolean);
    const roleBasePath = pathSegments[0];
    navigate(`/${roleBasePath}/calendar/${calendar.year}`);
  };

  // Show loading state
  if (isLoading) {
    return (
      <div className="h-full bg-white flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-gray-400 mx-auto mb-4" />
          <p className="text-gray-500">Loading calendars...</p>
        </div>
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div className="h-full bg-white flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="w-8 h-8 text-red-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            Error Loading Calendars
          </h3>
          <p className="text-gray-500 mb-4">
            {error?.data?.message ||
              "Failed to load calendars. Please try again."}
          </p>
          <button
            onClick={() => refetch()}
            className="px-4 py-2 bg-black text-white rounded hover:bg-gray-800 transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full bg-white">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 px-4 sm:px-6 py-4 border-b border-gray-200">
        <h1 className="text-xl sm:text-2xl font-semibold text-gray-900">
          Holiday Calendars
        </h1>

        {canEdit && (
          <button
            onClick={handleCreateCalendar}
            className="flex items-center space-x-2 px-3 py-2 sm:px-4 sm:py-2 bg-black text-white rounded hover:opacity-80 transition-all text-sm"
          >
            <Plus className="w-4 h-4" />
            <span className="text-xs sm:text-sm">New Calendar</span>
          </button>
        )}
      </div>

      {/* Calendar Grid */}
      <div className="p-4 sm:p-6">
        {!Array.isArray(calendars) || calendars.length === 0 ? (
          <div className="text-center py-12 sm:py-16">
            <div className="w-20 h-20 sm:w-24 sm:h-24 mx-auto mb-4 sm:mb-6 bg-gray-100 rounded-full flex items-center justify-center">
              <CalendarIcon className="w-10 h-10 sm:w-12 sm:h-12 text-gray-400" />
            </div>
            <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-2 sm:mb-3">
              No Calendars Found
            </h3>
            <p className="text-sm sm:text-base text-gray-500 mb-6 sm:mb-8 max-w-md mx-auto px-4">
              You haven't created any holiday calendars yet. Create your first
              calendar to manage holidays and leave policies for your academy.
            </p>
            {canEdit && (
              <button
                onClick={handleCreateCalendar}
                className="inline-flex items-center space-x-2 px-4 py-2 sm:px-6 sm:py-3 bg-black text-white rounded-lg hover:bg-gray-800 transition-colors duration-200 font-medium text-sm sm:text-base"
              >
                <Plus className="w-4 h-4 sm:w-5 sm:h-5" />
                <span>Create Your First Calendar</span>
              </button>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4 sm:gap-6">
            {Array.isArray(calendars) &&
              calendars.map((calendar) => (
                <div
                  key={calendar._id || calendar.id}
                  className="bg-white border border-gray-200 rounded-lg hover:border-gray-300 hover:shadow-md transition-all duration-200 cursor-pointer group"
                  onClick={() => handleCalendarClick(calendar)}
                >
                  <div className="p-4 sm:p-6">
                    <div className="flex items-center justify-between mb-3 sm:mb-4">
                      <div className="flex items-center space-x-2 sm:space-x-3 flex-1 min-w-0">
                        <div
                          className={`w-3 h-3 sm:w-4 sm:h-4 rounded-full flex-shrink-0 ${
                            calendar.year === new Date().getFullYear()
                              ? "bg-blue-500"
                              : "bg-gray-400"
                          }`}
                        />
                        <span className="text-xs sm:text-sm font-semibold text-gray-900 truncate">
                          {calendar.year}
                        </span>
                        {calendar.year === new Date().getFullYear() && (
                          <span className="px-1.5 py-0.5 sm:px-2 sm:py-1 text-xs font-medium bg-blue-100 text-blue-800 rounded-full flex-shrink-0">
                            Current
                          </span>
                        )}
                      </div>
                      <div className="flex items-center space-x-1 flex-shrink-0">
                        {canEdit && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteCalendar(calendar._id || calendar.id);
                            }}
                            className="p-1.5 sm:p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors duration-200 opacity-0 group-hover:opacity-100"
                            title="Delete Calendar"
                            disabled={isDeleting}
                          >
                            <Trash2 className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                          </button>
                        )}
                        <ChevronRight className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-gray-400 group-hover:text-gray-600 transition-colors duration-200" />
                      </div>
                    </div>

                    <h3 className="font-semibold text-gray-900 mb-3 sm:mb-4 truncate text-base sm:text-lg">
                      {calendar.name}
                    </h3>

                    <div className="flex items-center justify-between mt-4 gap-2">
                      <span className="text-xs sm:text-sm text-gray-600 whitespace-nowrap">
                        {calendar.holiday_count || 0} holidays
                      </span>
                      <span className="text-xs sm:text-sm text-gray-600 whitespace-nowrap ml-auto">
                        {calendar.working_days || 0} working days
                      </span>
                    </div>
                  </div>
                </div>
              ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default CalendarOverview;
